import {
  DatePicker,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  Tabs,
  Tooltip,
} from 'antd';
import styles from './dabaProReport.module.scss';
import backIcon from 'images/back.png';
import {useNavigate, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {Viewer, Worker} from '@react-pdf-viewer/core';
import FadeEffect from 'components/animation/fadeEffect';
import {Button} from '@dabafinance/react-components';
import {defaultLayoutPlugin} from '@react-pdf-viewer/default-layout';
import {toast} from 'react-toastify';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import moment from 'moment';

import {defaultLanguages} from 'pages/news/newsTypes';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';

import {formatRoleString} from 'utils/helper';
import CompanyPDFUpload from './components/CompanyPDFUpload';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  GET_ALL_COMPANY_DOCUMENT_CATEGORIES,
  GET_SINGLE_USER_DOCUMENT,
} from 'graphql/queries/companyDocument';
import {QuestionCircleOutlined} from '@ant-design/icons';
import {useDabaProReportsForm} from './hooks/useDabaProReportsForm';
import {
  CREATE_COMPANY_DOCUMENT_CENTER,
  UPDATE_COMPANY_DOCUMENT_CENTER,
} from 'graphql/mutations/documentCenter';
import {GET_ALL_STARTUPS} from 'graphql/queries/startupCompanies';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import _ from 'lodash';

export const CreateDabaProReport = () => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const {id} = useParams();
  const navigate = useNavigate();
  const {
    form,
    issueDate,
    setIssueDate,
    pdfFile,
    setPdfFile,
    loadingPdfUpload,
    baseLanguage,
    setBaseLanguage,
    documentName,
    setDocumentName,
    pdfLink,
    setPdfLink,
    viewPdf,
    pdfName,
    setPdfName,
    translatedData,
    setTranslatedData,
    selectedLanguage,
    setSelectedLanguage,
    handleDeleteFile,
    handlePdfFileSave,
    handleChangeTranslation,
    handlePdfFileChange,
    handleTranslation,
    handleDeleteTranslatedPDFFile,
  } = useDabaProReportsForm();
  const tag = Form.useWatch('tag', form);
  const [instruments, setInstruments] = useState([]);
  const [company, setCompany] = useState([]);

  const {data, loading} = useQuery(GET_SINGLE_USER_DOCUMENT, {
    variables: {
      documentId: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  });

  const [getAllCompanies, {loading: loadingCompanies}] = useLazyQuery(
    GET_ALL_STARTUPS,
    {
      onError: error => toast.error(error.message),
      errorPolicy: 'all',
    },
  );

  const {loading: catLoading, data: catData} = useQuery(
    GET_ALL_COMPANY_DOCUMENT_CATEGORIES,
  );

  const [
    fetchInstruments,
    {refetch: refetchInstrument, loading: loadingInstruments},
  ] = useLazyQuery(GET_ASSET_CLASS_INSTRUMENTS);

  useEffect(() => {
    if (id && data?.getOneCompanyDocument?.data) {
      setPdfFile(data?.getOneCompanyDocument?.data?.documentUrl);
      setPdfLink(data?.getOneCompanyDocument?.data?.documentUrl);
      setDocumentName(data?.getOneCompanyDocument?.data?.documentName);
      setPdfName(data?.getOneCompanyDocument?.data?.documentName);
      setBaseLanguage(data?.getOneCompanyDocument?.data?.language);
      setSelectedLanguage(data?.getOneCompanyDocument?.data?.language);
      setTranslatedData(
        data?.getOneCompanyDocument?.data?.multilang?.map(data => ({
          documentName: data?.documentName,
          pdfFile: data?.documentUrl,
          pdfLink: data?.documentUrl,
          language: data?.language,
          pdfName: data?.documentName,
          viewPdf: data?.viewPdf,
        })),
      );

      form.setFieldsValue({
        documentName: data?.getOneCompanyDocument?.data?.documentName,
        issuedDate: moment(
          data?.getOneCompanyDocument?.data?.issuedDate,
          'YYYY-MM-DD HH:mm:ss',
        ),
        company: data?.getOneCompanyDocument?.data?.company?.id,
        instrumentId: data?.getOneCompanyDocument?.data?.bond?.id,
        languageRestriction:
          data?.getOneCompanyDocument?.data?.restrictLanguage,
        baseLanguage: data?.getOneCompanyDocument?.data?.language,
        documentCategory:
          data?.getOneCompanyDocument?.data?.documentCategory?.id,
        tag: data?.getOneCompanyDocument?.data?.tag,
      });
    }
  }, [data?.getOneCompanyDocument?.data, id]);

  useEffect(() => {
    if (tag === 'STOCK') {
      getAllCompanies({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
        onCompleted: data => setCompany(data?.getAllCompanies || []),
      });
    } else if (tag === 'BOND') {
      fetchInstruments({
        variables: {
          input: {
            explore: false,
            instrumentKey: 'BOND',
            assetClassKey: 'FIXED_INCOMES',
            search: data?.getOneCompanyDocument?.data?.bond?.name,
            page: 1,
            limit: 100,
          },
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
        onCompleted: data =>
          setInstruments(data?.getFinancialInstruments?.data || []),
      });
    }
  }, [tag]);

  const handleSearch = _.debounce(async value => {
    try {
      const {data} = await refetchInstrument({
        input: {
          explore: false,
          instrumentKey: 'BOND',
          assetClassKey: 'FIXED_INCOMES',
          search: value,
          page: 1,
          limit: 100,
        },
      });
      setInstruments(data.getFinancialInstruments?.data || []);
    } catch (error) {
      console.error('Error fetching instruments:', error);
    }
  }, 1200);

  const [addDabaProReport, {loading: loadingCreate}] = useMutation(
    CREATE_COMPANY_DOCUMENT_CENTER,
  );
  const [updateDabaProReport, {loading: loadingUpdate}] = useMutation(
    UPDATE_COMPANY_DOCUMENT_CENTER,
  );

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );

  const languageToBeTranslatedTo = defaultLanguages.filter(
    key => key.key !== baseLanguage,
  );

  const handleSubmit = values => {
    if (
      translatedData.length &&
      translatedData.some(
        data => !data || !data.documentName || !data.pdfFile || !data.language,
      )
    ) {
      return toast.error(
        'Translated data must have documentName, pdfFile, and language',
      );
    }
    if (!pdfLink) return toast.error('PDF file must be uploaded');

    if (id) {
      updateDabaProReport({
        variables: {
          data: {
            documentId: id,
            documentUrlSource: 'UPLOADED',
            language: baseLanguage,
            company: values?.company,
            bond: values?.instrumentId,
            isDabaPro: true,
            documentCategory: values?.documentCategory,
            restrictLanguage:
              values?.languageRestriction != null
                ? values?.languageRestriction
                : false,
            documentName: values?.documentName,
            documentUrl: pdfLink,
            tag: values?.tag,
            issuedDate:
              data?.getOneCompanyDocument?.data?.issuedDate ??
              moment(issueDate).format(),
            multilang: translatedData.length
              ? translatedData?.map(data => ({
                  documentName: data?.documentName,
                  documentUrl: data?.pdfFile,
                  documentUrlSource: 'UPLOADED',
                  language: data?.language,
                }))
              : [],
          },
        },
      })
        .then(({data: {updateCompanyDocument}}) => {
          if (updateCompanyDocument.__typename === 'Error') {
            return toast.error(updateCompanyDocument.message);
          } else {
            toast.success('Updated Successfully');
            navigate(-1);
          }
        })
        .catch(error => toast.error(error));
    } else {
      addDabaProReport({
        variables: {
          data: {
            language: baseLanguage,
            documentCategory: values?.documentCategory,
            restrictLanguage:
              values?.languageRestriction != null
                ? values?.languageRestriction
                : false,
            documentName: values?.documentName,
            documentUrlSource: 'UPLOADED',
            documentUrl: pdfLink,
            company: values?.company,
            bond: values?.instrumentId,
            isDabaPro: true,
            tag: values?.tag,
            issuedDate: moment(issueDate).format(),
            multilang: translatedData?.length
              ? translatedData?.map(data => ({
                  documentName: data?.documentName,
                  documentUrl: data?.pdfFile,
                  documentUrlSource: 'UPLOADED',
                  language: data?.language,
                }))
              : [],
          },
        },
      })
        .then(({data: {addCompanyDocument}}) => {
          if (addCompanyDocument.__typename === 'Error') {
            return toast.error(addCompanyDocument.message);
          } else {
            toast.success('Created Successfully');
            navigate(-1);
          }
        })
        .catch(error => toast.error(error));
    }
  };

  if (loading && id) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading Report...</h1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.nav}>
        <img onClick={() => navigate(-1)} src={backIcon} alt="back" />
        <h1>{id ? 'Update' : 'Create'} Daba Pro Report</h1>
      </div>
      <div className={styles.form}>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            rules={[{required: true, message: 'This is a required field'}]}
            className={styles.field}
            name="tag"
            label="Report Tag">
            <Select loading={catLoading}>
              <Select.Option key="STOCK">STOCK</Select.Option>
              <Select.Option key="BOND">BOND</Select.Option>
              <Select.Option key="MACRO">MACRO</Select.Option>
            </Select>
          </Form.Item>
          {tag === 'STOCK' && (
            <Form.Item
              className={styles.field}
              name="company"
              label="Select Company">
              <Select
                listHeight={400}
                loading={loadingCompanies}
                allowClear
                optionFilterProp="label"
                filterOption
                disabled={loadingCompanies}
                showSearch
                className={styles['select-height']}>
                {loadingCompanies ? (
                  <Select.Option key="Loading">Loading..</Select.Option>
                ) : (
                  company?.map(company => (
                    <Select.Option
                      label={`${company?.name} (${company?.ticker})`}
                      key={company?.id}>
                      <div className={styles['all-data']}>
                        <img src={company?.logoImgURL} alt="" />
                        <span>{company?.name}</span>
                      </div>
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          )}
          {tag === 'BOND' && (
            <Form.Item
              name="instrumentId"
              className={styles.field}
              label="Select Instrument">
              <Select
                showSearch
                onSearch={handleSearch}
                filterOption={false}
                loading={loadingInstruments}
                allowClear>
                {instruments.map(instrument => (
                  <Select.Option key={instrument?.id}>
                    {instrument?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            className={styles.field}
            name="baseLanguage"
            rules={[{required: true, message: 'This is a required field'}]}
            label="Base Language">
            <Select value={baseLanguage} onChange={setBaseLanguage}>
              {defaultLanguages.map(data => (
                <Select.Option key={data.key}>{data.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.field}
            name="documentCategory"
            label="Document Category">
            <Select loading={catLoading}>
              {catData?.getCompanyDocumentCategories?.data?.map(data => (
                <Select.Option key={data?.id}>
                  {formatRoleString(data?.name)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className={styles.flexWidth}>
            <Form.Item
              rules={[{required: true, message: 'This Field is Required'}]}
              name="issuedDate"
              label="Issued Date">
              <DatePicker
                name="issuedDate"
                value={issueDate}
                className={styles.date}
                format={'YYYY-MM-DD HH:mm:ss'}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                onChange={e =>
                  setIssueDate(moment(e).format('YYYY-MM-DD HH:mm:ss'))
                }
              />
            </Form.Item>
            <Form.Item
              name="languageRestriction"
              label={
                <span>
                  Language Restriction{' '}
                  <Tooltip
                    title={
                      'This Restricts Users from viewing Document that is not in their Language Preference'
                    }>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }>
              <Switch
                defaultChecked={
                  data?.getOneCompanyDocument?.data?.restrictLanguage
                }
              />
            </Form.Item>
          </div>
          <Form.Item
            label="Translation Language"
            name="translationLanguage"
            className={styles.field}>
            <Select
              value={selectedLanguage}
              className={styles.field}
              onChange={e => setSelectedLanguage(e)}
              placeholder="Select a Language to be translated">
              {languageToBeTranslatedTo.map(name => (
                <Select.Option key={name.key}>{name.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Tabs
            style={{width: 800}}
            tabBarExtraContent={
              selectedLanguage && (
                <Button
                  label={loadingTranslation ? 'Translating...' : 'Translate'}
                  disabled={loadingTranslation || !documentName}
                  onClick={() => handleTranslation(translateText)}
                />
              )
            }>
            <Tabs.TabPane className="" key="Original" tab="Original">
              <Form.Item
                rules={[{required: true, message: 'This is a required field'}]}
                name="documentName"
                label="Document Name">
                <Input
                  className={styles.field}
                  name={documentName}
                  onChange={e => setDocumentName(e.target.value)}
                  placeholder="Input the Document Name"
                />
              </Form.Item>
              <h1 className={styles.documentHeader}>Upload Daba Pro Report</h1>
              {!viewPdf && (
                <form>
                  <label className={styles.upload} htmlFor="file-upload">
                    {loadingPdfUpload ? (
                      <div className={styles.spinner}>
                        <Spin size="large" />
                        <h3>Uploading...</h3>
                      </div>
                    ) : (
                      <span> {pdfName}</span>
                    )}
                    <input
                      type="file"
                      id="file-upload"
                      accept="application/pdf"
                      className={styles['input-file']}
                      required
                      onChange={handlePdfFileChange}
                    />
                  </label>
                </form>
              )}
              {viewPdf && (
                <FadeEffect>
                  <div className={styles['pdf-height']}>
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
                      <Viewer
                        fileUrl={viewPdf}
                        plugins={[defaultLayoutPluginInstance]}
                      />
                    </Worker>
                  </div>
                </FadeEffect>
              )}
              <div className={styles['button-wrapper']}>
                <Button
                  disabled={pdfFile === ''}
                  label="Preview PDF"
                  onClick={handlePdfFileSave}
                />
                <Button
                  type="secondary"
                  label="Delete"
                  onClick={handleDeleteFile}
                />
              </div>
            </Tabs.TabPane>
            {translatedData?.map((item, index) => (
              <Tabs.TabPane
                key={index}
                tab={
                  languageToBeTranslatedTo.find(
                    lang => lang?.key === item?.language,
                  )?.label
                }>
                <Form.Item label="Translated Document Name">
                  <Input
                    value={item?.documentName}
                    className={styles.field}
                    onChange={e => handleChangeTranslation(index, e)}
                  />
                </Form.Item>
                <CompanyPDFUpload
                  title="Upload Translated Daba Pro Report"
                  setExistingData={setTranslatedData}
                  pdfName={item?.pdfName || 'Supported format: pdf'}
                  existingData={translatedData}
                  loadingPdfUpload={item?.loadingPdfUpload}
                  index={index}
                  pdfFile={item?.pdfFile}
                  handleDeleteFile={() => handleDeleteTranslatedPDFFile(index)}
                />
              </Tabs.TabPane>
            ))}
          </Tabs>
          <SubmitButton
            label={id ? 'Update' : 'Create'}
            disabled={loadingCreate || loadingUpdate}
          />
        </Form>
      </div>
    </div>
  );
};
